import { useState } from "react";
import { Info, Loader2 } from "lucide-react";

import { Meta } from "@/@types/paginated";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import { PaginationButtons } from "@/components/PaginatedDatatable/components/PaginationButtons";
import { useGetTabRowsQuery } from "@/services/tax-closing";
import type { TaxClosingTabs } from "@/services/tax-closing";
import { useSituationFilter } from "../hooks/use-situation-filter";

import { formatBRL } from "@/utils/money";
import { StateBadge } from "./state-badge";

type TabData = TaxClosingTabs["modeloCfopCst"];

interface ModeloCfopCstTabProps {
  data: TabData;
  jobId: string;
}

const CST_RULES: Record<string, { label: string; desc: string }> = {
  "00": { label: "Tributada integralmente", desc: "ICMS cobrado normal. Base ICMS = valor da mercadoria menos descontos." },
  "10": { label: "Tributada + ICMS por ST", desc: "ICMS próprio + recolhimento antecipado por substituição tributária." },
  "20": { label: "Redução de base", desc: "Base de cálculo reduzida por benefício fiscal." },
  "40": { label: "Isenta", desc: "Mercadoria isenta de ICMS. Base e Valor = 0." },
  "41": { label: "Não tributada", desc: "Fora do campo de incidência do ICMS." },
  "60": { label: "ICMS cobrado por ST anterior", desc: "ICMS já recolhido na cadeia anterior (indústria/distribuidor)." },
  "90": { label: "Outros", desc: "Regime especial, incentivo fiscal ou operação não enquadrada." },
};

export function ModeloCfopCstTab({ data, jobId }: ModeloCfopCstTabProps) {
  return (
    <div className="flex flex-col gap-8">
      {/* Table 1: Modelo x CFOP */}
      <section>
        <h3 className="text-base font-semibold mb-3">Modelo x CFOP</h3>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="py-2 px-3 text-left font-medium text-muted-foreground">Modelo</th>
                <th className="py-2 px-3 text-left font-medium text-muted-foreground">CFOP</th>
              </tr>
            </thead>
            <tbody>
              {data.modeloCfop.map((row, i) => (
                <tr key={i} className="border-b hover:bg-muted/30">
                  <td className="py-2 px-3">{row.modelo}</td>
                  <td className="py-2 px-3">{row.cfop}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Table 2: CST x Tributação */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-base font-semibold">CST x Tributação ICMS</h3>
          <CstInfoDialog cstCodes={data.cstTributacao.map((r) => String(r.cstIcms))} />
        </div>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="py-2 px-3 text-left font-medium text-muted-foreground">CST</th>
                <th className="py-2 px-3 text-right font-medium text-muted-foreground">Soma de NOVA BASE</th>
                <th className="py-2 px-3 text-right font-medium text-muted-foreground">Soma de Base ICMS</th>
                <th className="py-2 px-3 text-right font-medium text-muted-foreground">Soma de Valor ICMS</th>
                <th className="py-2 px-3 text-center font-medium text-muted-foreground" />
              </tr>
            </thead>
            <tbody>
              {data.cstTributacao.map((row, i) => (
                <tr key={i} className="border-b hover:bg-muted/30">
                  <td className="py-2 px-3">
                    <span className="font-mono">{row.cstIcms}</span>
                    {CST_RULES[String(row.cstIcms)] && (
                      <span className="text-xs text-muted-foreground ml-2">
                        {CST_RULES[String(row.cstIcms)].label}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">{formatBRL(row.sumNovaBaseIcms)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{formatBRL(row.sumBaseIcms)}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{formatBRL(row.sumValorIcms)}</td>
                  <td className="py-2 px-3 text-center">
                    <CompositionDialog
                      cst={String(row.cstIcms)}
                      jobId={jobId}
                      sumNovaBase={row.sumNovaBaseIcms}
                      sumBaseIcms={row.sumBaseIcms}
                      sumValorIcms={row.sumValorIcms}
                    />
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 bg-muted/60 font-bold">
                <td className="py-2 px-3">{data.cstTributacaoTotal.cstIcms}</td>
                <td className="py-2 px-3 text-right tabular-nums">{formatBRL(data.cstTributacaoTotal.sumNovaBaseIcms)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{formatBRL(data.cstTributacaoTotal.sumBaseIcms)}</td>
                <td className="py-2 px-3 text-right tabular-nums">{formatBRL(data.cstTributacaoTotal.sumValorIcms)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Table 3: UF x CFOP */}
      <section>
        <h3 className="text-base font-semibold mb-3">UF x CFOP</h3>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="py-2 px-3 text-left font-medium text-muted-foreground">UF</th>
                <th className="py-2 px-3 text-left font-medium text-muted-foreground">CFOP</th>
              </tr>
            </thead>
            <tbody>
              {data.ufCfop.map((row, i) => (
                <tr key={i} className="border-b hover:bg-muted/30">
                  <td className="py-2 px-3"><StateBadge uf={row.ufFornCliente} /></td>
                  <td className="py-2 px-3 font-mono text-xs">{row.cfop}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// -- (!) Modal explicativo --
function CstInfoDialog({ cstCodes }: { cstCodes: string[] }) {
  const unique = [...new Set(cstCodes)];
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-6 w-6 p-0 rounded-full">
          <Info className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>CST ICMS — O que significa cada código</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm mt-2">
          {unique.map((code) => {
            const rule = CST_RULES[code];
            return (
              <div key={code} className="flex gap-3 items-start">
                <Badge variant="outline" className="font-mono shrink-0 mt-0.5">{code}</Badge>
                <div>
                  <span className="font-medium">{rule?.label ?? "Desconhecido"}</span>
                  <p className="text-xs text-muted-foreground mt-0.5">{rule?.desc ?? ""}</p>
                </div>
              </div>
            );
          })}
          <hr />
          <div className="text-xs text-muted-foreground">
            <p><strong>NOVA BASE</strong> = Σ (vlrTotal - vlrDesconto) por item</p>
            <p><strong>Base ICMS</strong> = Σ baseIcms (declarado pelo emissor na NF-e)</p>
            <p><strong>Valor ICMS</strong> = Σ valorIcms (declarado na NF-e)</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -- "Ver composição" — modal com rows reais + cálculo --
interface CompositionDialogProps {
  cst: string;
  jobId: string;
  sumNovaBase: string;
  sumBaseIcms: string;
  sumValorIcms: string;
}

function CompositionDialog({ cst, jobId, sumNovaBase, sumBaseIcms, sumValorIcms }: CompositionDialogProps) {
  const [open, setOpen] = useState(false);
  const [pagination, setPagination] = useState<Meta>({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0,
    prev: null,
    next: null,
  });

  const { filters: situationFilters } = useSituationFilter();

  const { data, isFetching } = useGetTabRowsQuery(
    {
      jobId,
      tab: "conferencias",
      page: pagination.currentPage,
      limit: pagination.perPage,
      filters: { cstIcms: [cst], ...situationFilters },
    },
    { skip: !open },
  );

  const rows = (data?.rows ?? []) as Record<string, any>[];

  // Sincroniza Meta do backend → Meta do PaginationButtons
  const currentMeta: Meta = {
    currentPage: pagination.currentPage,
    lastPage: data?.meta?.totalPages ?? 1,
    perPage: pagination.perPage,
    total: data?.meta?.totalFiltered ?? 0,
    prev: pagination.currentPage > 1 ? pagination.currentPage - 1 : null,
    next: pagination.currentPage < (data?.meta?.totalPages ?? 1) ? pagination.currentPage + 1 : null,
  };

  const baseIcms = parseFloat(String(sumBaseIcms).replace(",", ".")) || 0;
  const valorIcms = parseFloat(String(sumValorIcms).replace(",", ".")) || 0;
  const aliqEfetiva = baseIcms > 0 ? ((valorIcms / baseIcms) * 100).toFixed(2) : "0.00";

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setPagination((p) => ({ ...p, currentPage: 1 })); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-6 text-[11px] px-2">
          Ver composição
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Composição CST {cst}
            {CST_RULES[cst] && (
              <span className="font-normal text-sm text-muted-foreground ml-2">
                — {CST_RULES[cst].label}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Cálculo */}
        <div className="text-xs border rounded-md p-3 bg-muted/30 font-mono space-y-1 mt-2">
          <p>NOVA BASE  = Σ (vlrTotal - vlrDesconto) = <strong>{formatBRL(sumNovaBase)}</strong></p>
          <p>Base ICMS  = Σ baseIcms                 = <strong>{formatBRL(sumBaseIcms)}</strong></p>
          <p>Valor ICMS = Σ valorIcms                = <strong>{formatBRL(sumValorIcms)}</strong></p>
          {baseIcms > 0 && (
            <p>Alíq. efetiva = {formatBRL(sumValorIcms)} / {formatBRL(sumBaseIcms)} = <strong>{aliqEfetiva}%</strong></p>
          )}
        </div>

        {/* Tabela */}
        <div className="rounded-md border mt-3">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nota</TableHead>
                <TableHead>CFOP</TableHead>
                <TableHead className="text-right">Vlr Total</TableHead>
                <TableHead className="text-right">Vlr Desconto</TableHead>
                <TableHead className="text-right">NOVA BASE</TableHead>
                <TableHead className="text-right">Base ICMS</TableHead>
                <TableHead className="text-right">Alíq ICMS</TableHead>
                <TableHead className="text-right">Valor ICMS</TableHead>
                <TableHead className="text-right">DIF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono">{r.numNota}</TableCell>
                    <TableCell className="font-mono">{r.cfop}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatBRL(r.vlrTotal)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatBRL(r.vlrDesconto)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatBRL(r.novaBaseIcms)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatBRL(r.baseIcms)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.aliqIcms ? `${r.aliqIcms}%` : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatBRL(r.valorIcms)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatBRL(r.dif)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className="h-24 text-center text-muted-foreground">
                    Nenhum item encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Paginação */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm text-muted-foreground">
            {currentMeta.total.toLocaleString("pt-BR")} linha(s) encontrada(s).
          </span>
          <PaginationButtons pagination={currentMeta} setPagination={setPagination} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
